import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { ChartBarIcon, CheckCircleIcon, XCircleIcon, BeakerIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';

const schema = z.object({
  measurement_id: z
    .string()
    .trim()
    .refine(v => v === '' || /^G-[A-Z0-9]{6,}$/.test(v), {
      message: 'Must be a valid GA4 Measurement ID (e.g. G-XXXXXXXXXX) or empty to disable'
    }),
  enabled: z.boolean()
});

type FormData = z.infer<typeof schema>;

interface GAStatus {
  loaded: boolean;
  trackingActive: boolean;
}

function detectGAStatus(measurementId: string): GAStatus {
  if (!measurementId) return { loaded: false, trackingActive: false };
  const win = window as any;
  const loaded = typeof win.gtag === 'function';
  const trackingActive = loaded && Array.isArray(win.dataLayer) && win.dataLayer.length > 0;
  return { loaded, trackingActive };
}

export default function GoogleAnalyticsForm() {
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settingId, setSettingId] = useState<string | null>(null);
  const [enabledSettingId, setEnabledSettingId] = useState<string | null>(null);
  const [status, setStatus] = useState<GAStatus>({ loaded: false, trackingActive: false });

  const { register, handleSubmit, reset, watch, formState: { errors, isDirty } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { measurement_id: '', enabled: false }
  });

  const measurementId = watch('measurement_id');
  const enabled = watch('enabled');

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    setStatus(detectGAStatus(measurementId));
  }, [measurementId]);

  const loadSettings = async () => {
    const { data } = await supabase
      .from('system_settings')
      .select('*')
      .eq('category', 'analytics');

    if (data) {
      const idSetting = data.find(s => s.key === 'ga_measurement_id');
      const enabledSetting = data.find(s => s.key === 'ga_enabled');

      if (idSetting) {
        setSettingId(idSetting.id);
        reset({
          measurement_id: idSetting.value || '',
          enabled: enabledSetting?.value === true || enabledSetting?.value === 'true'
        });
      }
      if (enabledSetting) setEnabledSettingId(enabledSetting.id);
    }
  };

  const upsert = async (key: string, value: any, existingId: string | null, setId: (id: string) => void) => {
    if (existingId) {
      const { error } = await supabase
        .from('system_settings')
        .update({ value, updated_at: new Date().toISOString() })
        .eq('id', existingId);
      if (error) throw error;
    } else {
      const { data, error } = await supabase
        .from('system_settings')
        .insert([{ key, value, category: 'analytics', description: key === 'ga_measurement_id' ? 'Google Analytics 4 Measurement ID' : 'Whether Google Analytics is enabled', is_public: false }])
        .select()
        .single();
      if (error) throw error;
      if (data) setId(data.id);
    }
  };

  const onSubmit = async (values: FormData) => {
    setSaving(true);
    try {
      await upsert('ga_measurement_id', values.measurement_id, settingId, setSettingId);
      await upsert('ga_enabled', values.enabled, enabledSettingId, setEnabledSettingId);
      toast.success('Google Analytics settings saved. Reload the page to apply changes.');
      reset(values);
    } catch (err) {
      toast.error('Failed to save Google Analytics settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEvent = async () => {
    setTesting(true);
    try {
      const win = window as any;
      if (typeof win.gtag !== 'function') {
        toast.error('Google Analytics is not loaded on this page yet. Save and reload first.');
        return;
      }
      win.gtag('event', 'settings_test', {
        event_category: 'Admin',
        event_label: 'GA Test from Settings',
        value: 1
      });
      toast.success('Test event sent! Check your GA4 Realtime report in a few seconds.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <ChartBarIcon className="w-6 h-6 text-golf-orange" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Google Analytics</h2>
          <p className="text-sm text-gray-500">Connect your GA4 property to track visitor behaviour</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            GA4 Measurement ID
          </label>
          <input
            {...register('measurement_id')}
            type="text"
            placeholder="G-XXXXXXXXXX"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange font-mono text-sm"
          />
          {errors.measurement_id && (
            <p className="mt-1 text-sm text-red-600">{errors.measurement_id.message}</p>
          )}
          <p className="mt-1 text-xs text-gray-400">
            Find this in your Google Analytics property under Admin &rarr; Data Streams &rarr; your stream.
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <input
            {...register('enabled')}
            type="checkbox"
            id="ga_enabled"
            className="w-4 h-4 text-golf-orange border-gray-300 rounded focus:ring-golf-orange"
          />
          <label htmlFor="ga_enabled" className="text-sm font-medium text-gray-700">
            Enable tracking
          </label>
        </div>

        {measurementId && (
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Current status</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <StatusBadge active={status.loaded} label="Script loaded" />
              <StatusBadge active={status.trackingActive} label="Tracking active" />
            </div>
            <p className="mt-3 text-xs text-gray-400">
              Status is detected from the current browser session. If you just saved, reload the page for accurate status.
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="submit"
            disabled={saving || !isDirty}
            className="px-4 py-2 bg-golf-orange text-white rounded-lg text-sm font-medium hover:bg-golf-orange/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>

          <button
            type="button"
            onClick={sendTestEvent}
            disabled={testing || !measurementId || !enabled}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <BeakerIcon className="w-4 h-4" />
            <span>{testing ? 'Sending...' : 'Send Test Event'}</span>
          </button>
        </div>
      </form>

      <div className="mt-8 border-t border-gray-100 pt-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">How it works</h3>
        <ol className="space-y-2 text-sm text-gray-500 list-decimal list-inside">
          <li>Enter your GA4 Measurement ID and enable tracking, then save.</li>
          <li>Reload the site — the GA4 script will be injected automatically.</li>
          <li>Use "Send Test Event" to verify events appear in GA4 Realtime.</li>
          <li>All page views are tracked automatically via React Router.</li>
        </ol>
      </div>
    </div>
  );
}

function StatusBadge({ active, label }: { active: boolean; label: string }) {
  return (
    <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-medium ${active ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
      {active
        ? <CheckCircleIcon className="w-4 h-4" />
        : <XCircleIcon className="w-4 h-4" />}
      <span>{label}</span>
    </div>
  );
}
